import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode, useRef } from 'react';
import { User } from '../types';
import { supabase, db, authApi } from '../services';
import { getAvatarUrl } from '../utils/formatters';
import { Role } from '../types';
import { useAppVisibility } from '../hooks/useAppVisibility';

// ============================================
// Types
// ============================================

interface AuthContextType {
  currentUser: User | null;
  setCurrentUser: (user: User | null) => void;
  isLoading: boolean;
  isInvited: boolean;
  authError: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
}

// ============================================
// Context
// ============================================

const AuthContext = createContext<AuthContextType | null>(null);

// ============================================
// Provider
// ============================================

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInvited, setIsInvited] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const { isVisible, wasHidden, resetWasHidden } = useAppVisibility();
  const lastSessionCheckRef = useRef<number>(Date.now());

  // Sync user profile from auth session
  const syncProfile = useCallback(async (sessionUser: any) => {
    if (!sessionUser) {
      setIsLoading(false);
      return;
    }

    try {
      console.log('[AuthContext] Syncing profile for user:', sessionUser.id);
      const meta = sessionUser.user_metadata || {};
      const invited = !!meta.is_invited;
      setIsInvited(invited);

      let profile: User | null = null;

      // Try to get existing profile - handle 403/RLS errors gracefully
      try {
        console.log('[AuthContext] Fetching user profile from database...');
        profile = await db.getUser(sessionUser.id);
        console.log('[AuthContext] Profile fetched:', profile ? 'Success' : 'Not found');
      } catch (err: any) {
        // 403 means RLS blocked access or user doesn't exist - we'll create them
        console.log('[AuthContext] Profile fetch failed (will create new):', err.message);
        profile = null;
      }

      if (!profile) {
        // Create new profile for first-time users or if RLS blocked
        const newProfile: User = {
          id: sessionUser.id,
          email: sessionUser.email || '',
          name: meta.full_name || 'New Member',
          role: meta.role || Role.TEAM_MEMBER,
          avatar: getAvatarUrl(sessionUser.id),
        };
        
        try {
          await db.createUser(newProfile);
          setCurrentUser(newProfile);
        } catch (createErr: any) {
          // If create fails due to duplicate, try fetching again
          if (createErr.message?.includes('duplicate') || createErr.code === '23505') {
            const existingProfile = await db.getUser(sessionUser.id);
            if (existingProfile) {
              setCurrentUser(existingProfile);
            } else {
              // Use local profile data if all else fails
              setCurrentUser(newProfile);
            }
          } else {
            // For invited users, we can proceed with local data
            // They just need to set password first
            console.warn('Could not create user record:', createErr);
            setCurrentUser(newProfile);
          }
        }
      } else {
        // Sync role if changed in auth metadata
        if (profile.role !== meta.role && meta.role) {
          const updatedProfile = { ...profile, role: meta.role };
          try {
            await db.updateUser(profile.id, { role: meta.role });
          } catch (e) {
            console.warn('Role sync failed:', e);
          }
          setCurrentUser(updatedProfile);
        } else {
          setCurrentUser(profile);
        }
      }
    } catch (err) {
      console.error('Critical Profile Sync Error:', err);
      // Don't block the user - set a minimal profile from auth data
      const meta = sessionUser.user_metadata || {};
      setCurrentUser({
        id: sessionUser.id,
        email: sessionUser.email || '',
        name: meta.full_name || 'User',
        role: meta.role || Role.TEAM_MEMBER,
        avatar: getAvatarUrl(sessionUser.id),
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Track if initial auth has been completed to avoid duplicate syncs
  const initialAuthDoneRef = useRef(false);

  // Initialize auth on mount - only runs once
  useEffect(() => {
    // Prevent double initialization in strict mode
    if (initialAuthDoneRef.current) return;

    const initAuth = async () => {
      try {
        console.log('[AuthContext] Initializing authentication...');
        const { session } = await authApi.getSession();
        console.log('[AuthContext] Session retrieved:', session ? 'User logged in' : 'No session');
        if (session?.user) {
          await syncProfile(session.user);
          console.log('[AuthContext] Profile sync complete');
        } else {
          setIsLoading(false);
        }
        initialAuthDoneRef.current = true;
      } catch (err) {
        console.error('[AuthContext] Auth session check failed:', err);
        setIsLoading(false);
        initialAuthDoneRef.current = true;
      }
    };

    initAuth();

    // Subscribe to auth state changes - this subscription is stable and doesn't need syncProfile dependency
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('[AuthContext] Auth state changed:', event);

      // Skip INITIAL_SESSION event as we handle it in initAuth
      if (event === 'INITIAL_SESSION') {
        return;
      }

      if (event === 'TOKEN_REFRESHED') {
        console.log('[AuthContext] Token refreshed successfully');
        return;
      }

      if (event === 'SIGNED_IN' && session?.user) {
        console.log('[AuthContext] User signed in');
        await syncProfile(session.user);
      } else if (event === 'SIGNED_OUT') {
        console.log('[AuthContext] User signed out');
        setCurrentUser(null);
        setIsInvited(false);
        setIsLoading(false);
      } else if (event === 'USER_UPDATED' && session?.user) {
        console.log('[AuthContext] User updated');
        await syncProfile(session.user);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency - only run once on mount

  // Refresh session when app becomes visible after being hidden
  useEffect(() => {
    if (!currentUser) return;

    if (isVisible && wasHidden) {
      const timeSinceLastCheck = Date.now() - lastSessionCheckRef.current;
      const MIN_CHECK_INTERVAL = 30000; // 30 seconds minimum between checks

      if (timeSinceLastCheck > MIN_CHECK_INTERVAL) {
        console.log('[AuthContext] App became visible, checking session...');

        const checkAndRefreshSession = async () => {
          try {
            const { data: { session }, error } = await supabase.auth.getSession();

            if (error) {
              console.error('[AuthContext] Session check failed:', error);
              return;
            }

            if (!session) {
              console.warn('[AuthContext] No session found after wake-up');
              // Session lost - user needs to log in again
              setCurrentUser(null);
              setIsInvited(false);
              return;
            }

            // Check if token needs refresh (expires within 5 minutes)
            const expiresAt = session.expires_at || 0;
            const now = Math.floor(Date.now() / 1000);
            const fiveMinutes = 5 * 60;

            if (expiresAt - now < fiveMinutes) {
              console.log('[AuthContext] Token expiring soon, refreshing...');
              const { error: refreshError } = await supabase.auth.refreshSession();
              if (refreshError) {
                console.error('[AuthContext] Token refresh failed:', refreshError);
              } else {
                console.log('[AuthContext] Token refreshed successfully');
              }
            } else {
              console.log('[AuthContext] Session valid, expires at:', new Date(expiresAt * 1000).toISOString());
            }

            lastSessionCheckRef.current = Date.now();
          } catch (e) {
            console.error('[AuthContext] Session check error:', e);
          }
        };

        checkAndRefreshSession();
        resetWasHidden();
      }
    }
  }, [isVisible, wasHidden, currentUser, resetWasHidden]);

  // Auth actions
  const signIn = useCallback(async (email: string, password: string) => {
    setAuthError(null);
    const result = await authApi.signIn(email, password);
    if (result.user) {
      await syncProfile(result.user);
    }
  }, [syncProfile]);

  const signOut = useCallback(async () => {
    await authApi.signOut();
    setCurrentUser(null);
    setIsInvited(false);
  }, []);

  const updatePassword = useCallback(async (password: string) => {
    await authApi.updatePassword(password);
    setIsInvited(false);
  }, []);

  const value: AuthContextType = {
    currentUser,
    setCurrentUser,
    isLoading,
    isInvited,
    authError,
    signIn,
    signOut,
    updatePassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// ============================================
// Hook
// ============================================

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export { AuthContext };
export default AuthContext;
